import React, { useRef, useState, useEffect } from "react";
import {
  Box,
  Typography,
  InputBase,
  Paper,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Switch,
  FormControlLabel,
  Tooltip,
  Tabs,
  Tab,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import { alpha } from "@mui/material/styles";
import axios from "axios";
import VideoCard from "../../components/VideoCard";
import CompilationPicker from "../../components/CompilationPicker";
import NativeVideoPicker from "../../components/NativeVideoPicker";
import api from "../../../../conf/api";
import DuplicateWarning from "../components/DuplicateWarning";
import { ApprovalStatus } from "../../Enums/enums";

export default function Step2VideoSelection({
  selectedBrands,
  brandVideos,
  onBrandVideosChange,
}) {
  const [searchResults, setSearchResults] = useState({});
  const [loading, setLoading] = useState({});
  const [aiVideoCheck, setAiVideoCheck] = useState({});
  const searchRefs = useRef({});

  // Per-brand video source tab: 0 = search WooGlobe, 1 = pick a compilation,
  // 2 = pick a CreatorVault-native video (Video Library).
  const [sourceTab, setSourceTab] = useState({});
  // Compilations are fetched, searched and filtered entirely by
  // CompilationPicker against /mrss/compilations/ — this step only says which
  // brand to scope to and what to do with a click. Only APPROVED compilations
  // have a video the publish pipeline will accept, so the picker is gated to
  // those rather than showing the rest disabled.

  // Native videos aren't brand-scoped (Video Library uploads have no brand
  // affiliation), so searching/filtering/fetching them is entirely
  // NativeVideoPicker's business — the same component Push to Admin Portal's
  // Step 3 renders, including the top-10 cap.
  const handleSourceTabChange = (brandName, tab) => {
    setSourceTab((s) => ({ ...s, [brandName]: tab }));
  };

  // Duplication check: { [unique_key]: { published, feeds: [...] } }
  const [publishStatus, setPublishStatus] = useState({});

  // Whenever the set of selected videos changes, look up publication status for
  // any keys we haven't checked yet (one batched request per change).
  useEffect(() => {
    const allKeys = Array.from(
      new Set(Object.values(brandVideos).flat().map((v) => v.unique_key)),
    );
    const missing = allKeys.filter((k) => k && !(k in publishStatus));
    if (missing.length === 0) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/mrss/video-publish-status/", {
          params: { keys: missing.join(",") },
        });
        const results = res.data?.results || {};
        if (cancelled) return;
        setPublishStatus((prev) => {
          const next = { ...prev };
          missing.forEach((k) => {
            next[k] = results[k] || { published: false, feeds: [] };
          });
          return next;
        });
      } catch {
        if (cancelled) return;
        // Mark as checked (not published) so we don't refetch in a loop
        setPublishStatus((prev) => {
          const next = { ...prev };
          missing.forEach((k) => {
            if (!(k in next)) next[k] = { published: false, feeds: [] };
          });
          return next;
        });
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandVideos]);

  const fetchVideos = async (brandName, searchText) => {
    if (!searchText?.trim()) return;
    setLoading((l) => ({ ...l, [brandName]: true }));
    try {
      const keys = searchText.split(",").map((k) => k.trim()).filter(Boolean);
      const requests = keys.map((key) => {
        const params = { search: key, limit: 20, orderType: "DES", page: 1 };
        if (aiVideoCheck[brandName]) {
          params.category_27 = "true";
          params.ai_based = "true";
        }
        return axios.get(process.env.REACT_APP_WEBSITE + "/apis/videos-filter", { params });
      });
      const responses = await Promise.all(requests);
      const all = responses.flatMap((r) => r.data);
      const unique = Array.from(new Map(all.map((v) => [v.unique_key, v])).values());
      setSearchResults((r) => ({ ...r, [brandName]: unique }));
    } catch {
      setSearchResults((r) => ({ ...r, [brandName]: [] }));
    } finally {
      setLoading((l) => ({ ...l, [brandName]: false }));
    }
  };

  const addVideo = (brandName, video) => {
    const current = brandVideos[brandName] || [];
    if (!current.find((v) => v.unique_key === video.unique_key)) {
      onBrandVideosChange({ ...brandVideos, [brandName]: [...current, video] });
    }
  };

  // Every search result not already added, in one change (adding them one by
  // one would each start from the same old list and keep only the last).
  const addAllVideos = (brandName, list) => {
    const current = brandVideos[brandName] || [];
    const fresh = list.filter((v) => !current.find((c) => c.unique_key === v.unique_key));
    if (fresh.length) onBrandVideosChange({ ...brandVideos, [brandName]: [...current, ...fresh] });
  };

  const removeVideo = (brandName, videoKey) => {
    onBrandVideosChange({
      ...brandVideos,
      [brandName]: (brandVideos[brandName] || []).filter((v) => v.unique_key !== videoKey),
    });
  };

  return (
    <Box sx={{ minWidth: 0, width: "100%" }}>
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        Step 2 — Select Videos
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Search and add videos for each brand.
      </Typography>

      {selectedBrands.map((brand) => {
        const videos = brandVideos[brand.name] || [];
        const results = searchResults[brand.name] || [];
        const isLoading = !!loading[brand.name];

        return (
          <Accordion key={brand.name} defaultExpanded variant="outlined" disableGutters sx={{ mb: 1.5, borderRadius: 2, "&:before": { display: "none" } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 56, px: { xs: 1.5, sm: 2 } }}>
              <Box display="flex" alignItems="center" flexWrap="wrap" gap={1} minWidth={0}>
                <Typography fontWeight={700} sx={{ wordBreak: "break-word" }}>{brand.name}</Typography>
                <Chip
                  icon={videos.length > 0 ? <CheckCircleIcon /> : undefined}
                  label={`${videos.length} video${videos.length !== 1 ? "s" : ""}`}
                  size="small"
                  color={videos.length > 0 ? "primary" : "default"}
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: { xs: 1.5, sm: 2, md: 3 }, pt: { xs: 0.5, sm: 1 }, minWidth: 0 }}>
              {/* Video source: search WooGlobe by WGID, pick a compilation, or pick a
                  CreatorVault-native video (Video Library upload) */}
              <Tabs
                value={sourceTab[brand.name] || 0}
                onChange={(_, v) => handleSourceTabChange(brand.name, v)}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                aria-label="Video source"
                sx={{ mb: 2, minHeight: 44 }}
              >
                <Tab label="Search WooGlobe" sx={{ minHeight: 44 }} />
                <Tab label="Use Compilation" sx={{ minHeight: 44 }} />
                <Tab label="Use Native Video" sx={{ minHeight: 44 }} />
              </Tabs>

              {(sourceTab[brand.name] || 0) === 0 && (
                <>
                  {/* Search bar */}
                  <Box display="flex" flexDirection={{ xs: "column", sm: "row" }} alignItems={{ xs: "stretch", sm: "center" }} gap={1} mb={2}>
                    <Paper variant="outlined" sx={{ display: "flex", alignItems: "center", flex: 1, minWidth: 0, "&:focus-within": { outline: "2px solid", outlineColor: "primary.main" } }}>
                      <InputBase
                        inputRef={(el) => { searchRefs.current[brand.name] = el; }}
                        placeholder="Search by title or ID…"
                        inputProps={{ "aria-label": `Search WooGlobe videos for ${brand.name}` }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter")
                            fetchVideos(brand.name, searchRefs.current[brand.name]?.value);
                        }}
                        sx={{ flex: 1, px: 2 }}
                      />
                      <IconButton
                        aria-label="Clear search"
                        sx={{ width: { xs: 44, sm: 36 }, height: { xs: 44, sm: 36 } }}
                        onClick={() => {
                          if (searchRefs.current[brand.name]) searchRefs.current[brand.name].value = "";
                          setSearchResults((r) => ({ ...r, [brand.name]: [] }));
                        }}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        aria-label="Search videos"
                        sx={{ width: { xs: 44, sm: 36 }, height: { xs: 44, sm: 36 } }}
                        onClick={() =>
                          fetchVideos(brand.name, searchRefs.current[brand.name]?.value)
                        }
                      >
                        <SearchIcon />
                      </IconButton>
                    </Paper>
                    <Tooltip title="Enable to search AI-generated videos only" arrow>
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={!!aiVideoCheck[brand.name]}
                            onChange={(e) =>
                              setAiVideoCheck((prev) => ({ ...prev, [brand.name]: e.target.checked }))
                            }
                            color="primary"
                          />
                        }
                        label="AI Videos"
                        sx={{ mr: 0, ml: { xs: 0.5, sm: 0 }, minHeight: 44, whiteSpace: "nowrap" }}
                      />
                    </Tooltip>
                  </Box>

                  {isLoading && (
                    <CircularProgress size={24} aria-label="Searching videos" sx={{ display: "block", mx: "auto", my: 1 }} />
                  )}

                  {/* Search results */}
                  {results.length > 0 && (
                    <Box mb={2}>
                      {(() => {
                        const left = results.filter((r) => !videos.find((v) => v.unique_key === r.unique_key)).length;
                        return (
                          <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} flexWrap="wrap">
                            <Typography variant="caption" color="text.secondary">
                              Click a result to add it, or add them all:
                            </Typography>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => addAllVideos(brand.name, results)}
                              disabled={left === 0}
                              sx={{ textTransform: "none", fontWeight: 600, borderRadius: 1.5, py: 0.25 }}
                            >
                              {left === 0 ? "All added" : `Select all (${left})`}
                            </Button>
                          </Box>
                        );
                      })()}
                      <Box
                        sx={{
                          display: "grid",
                          gap: 1,
                          mt: 0.5,
                          gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 260px), 1fr))",
                        }}
                      >
                        {results.map((video) => {
                          const already = !!videos.find((v) => v.unique_key === video.unique_key);
                          return (
                            <Box key={video.unique_key} sx={{ minWidth: 0 }}>
                              <Box
                                role="button"
                                tabIndex={already ? -1 : 0}
                                aria-disabled={already}
                                aria-label={`${already ? "Added" : "Add"} ${video.title || video.unique_key}`}
                                onClick={() => !already && addVideo(brand.name, video)}
                                onKeyDown={(e) => {
                                  if ((e.key === "Enter" || e.key === " ") && !already) {
                                    e.preventDefault();
                                    addVideo(brand.name, video);
                                  }
                                }}
                                sx={{
                                  position: "relative",
                                  cursor: already ? "default" : "pointer",
                                  opacity: already ? 0.6 : 1,
                                  border: "2px solid",
                                  borderColor: already ? "success.main" : "transparent",
                                  borderRadius: 1,
                                  "&:focus-visible": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: 2 },
                                }}
                              >
                                {already && (
                                  <CheckCircleIcon
                                    color="success"
                                    sx={{ position: "absolute", top: 8, right: 8, zIndex: 1, bgcolor: "background.paper", borderRadius: "50%" }}
                                  />
                                )}
                                <VideoCard video={video} _component="div" />
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  )}
                </>
              )}

              {(sourceTab[brand.name] || 0) === 1 && (
                <Box mb={2}>
                  <CompilationPicker
                    brandName={brand.name}
                    status={ApprovalStatus.APPROVED}
                    selectedKeys={videos.map((v) => v.unique_key)}
                    onSelect={(c) =>
                      addVideo(brand.name, {
                        unique_key: c.unique_key,
                        title: c.title,
                        description: c.description,
                        keywords: c.keywords || [],
                        s3_url: c.video_url,
                        is_compilation: true,
                        compilation_id: c.id,
                      })
                    }
                  />
                </Box>
              )}

              {(sourceTab[brand.name] || 0) === 2 && (
                <Box mb={2}>
                  <NativeVideoPicker
                    selectedKeys={videos.map((v) => v.unique_key)}
                    onSelect={(nv) => addVideo(brand.name, nv)}
                  />
                </Box>
              )}

              {videos.length === 0 && (
                <Alert severity="warning" icon={<VideoLibraryIcon />} sx={{ mb: 1 }}>
                  No videos selected for this brand — search above to add some.
                </Alert>
              )}

              {/* Selected videos */}
              {videos.map((video) => {
                const status = publishStatus[video.unique_key];
                const isDuplicate = !!status?.published;
                return (
                  <Paper
                    key={video.unique_key}
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      mb: 1,
                      borderColor: isDuplicate ? "warning.main" : "divider",
                      bgcolor: isDuplicate ? (t) => alpha(t.palette.warning.main, 0.06) : "background.paper",
                      borderLeft: isDuplicate ? "4px solid" : "1px solid",
                      borderLeftColor: isDuplicate ? "warning.main" : "divider",
                    }}
                  >
                    <Box display="flex" flexDirection={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }} gap={1}>
                      <Box display="flex" alignItems="center" gap={1} minWidth={0}>
                        <DuplicateWarning status={status} />
                        <Box minWidth={0}>
                          <Typography variant="body2" fontWeight={600} noWrap>
                            {video.title || video.video_title || video.unique_key}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {video.unique_key}
                          </Typography>
                        </Box>
                      </Box>
                      <Box display="flex" alignItems="center" justifyContent={{ xs: "space-between", sm: "flex-end" }} flexWrap="wrap" gap={0.5} flexShrink={0}>
                        {video.is_compilation && (
                          <Chip size="small" color="info" variant="outlined" label="Compilation" />
                        )}
                        {video.is_native_video && (
                          <Chip size="small" color="secondary" variant="outlined" label="Native Video" />
                        )}
                        {isDuplicate && (
                          <Chip
                            size="small"
                            color="warning"
                            variant="outlined"
                            label={`Published · ${status.feeds.length} feed${
                              status.feeds.length !== 1 ? "s" : ""
                            }`}
                          />
                        )}
                        <IconButton
                          size="small"
                          color="error"
                          aria-label={`Remove ${video.title || video.video_title || video.unique_key}`}
                          sx={{ width: { xs: 44, sm: 36 }, height: { xs: 44, sm: 36 } }}
                          onClick={() => removeVideo(brand.name, video.unique_key)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  </Paper>
                );
              })}
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
}
